import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { RequestStatus } from '@freelancer/datastore';
import {
  ContestFee,
  ContestFeesCollection,
  Currency,
} from '@freelancer/datastore/collections';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import { UpgradeType } from '@freelancer/ui/upgrade-tag';
import * as Rx from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export enum DurationType {
  URGENT = 'urgent',
  NONURGENT = 'nonurgent',
}

export const DEFAULT_CONTEST_DURATION = 7;
export const URGENT_CONTEST_DURATION = 1;

@Component({
  selector: 'app-contest-duration-type',
  template: `
    <fl-heading
      i18n="Post Job Page form contest duration type field label"
      [headingType]="HeadingType.H3"
      [flMarginBottom]="Margin.XXXSMALL"
      [size]="TextSize.MID"
    >
      Is your contest urgent?
    </fl-heading>
    <fl-banner-alert
      i18n="Post Job Page contest Urgent upgrade fee update error message"
      *ngIf="upgradeFeesStatus?.error"
      [closeable]="false"
      [flMarginBottom]="Margin.XSMALL"
    >
      The 1 day option is unavailable at the moment.
    </fl-banner-alert>
    <fl-bit [flMarginBottom]="Margin.XLARGE">
      <fl-grid [vAlign]="VerticalAlignment.VERTICAL_STRETCH">
        <fl-col
          [col]="12"
          [colTablet]="6"
          [flMarginBottom]="Margin.SMALL"
          [flMarginBottomTablet]="Margin.NONE"
        >
          <fl-radio-card
            flTrackingLabel="NonUrgent"
            i18n-pictureAlt="Nonurgent contest icon alt text"
            pictureAlt="Nonurgent contest icon"
            pictureSrc="job-post/nonurgent-contest-icon.svg"
            [control]="control"
            [isSelected]="isNonUrgentCardSelected$ | async"
            [value]="DurationType.NONURGENT"
            (click)="cardSelected.emit(DurationType.NONURGENT)"
          >
            <fl-radio-card-body>
              <fl-text
                i18n="
                   Post Job Page form contest duration type - nonurgent contest
                  option title
                "
                [size]="TextSize.SMALL"
                [weight]="FontWeight.BOLD"
              >
                3 to 30 days
              </fl-text>

              <fl-text
                i18n="
                   Post Job Page form contest duration type - nonurgent contest
                  option description
                "
                [flMarginBottom]="Margin.XSMALL"
              >
                Give freelancers plenty of time to submit quality entries. Best
                for getting the most creativity.
              </fl-text>
              <fl-text
                i18n="
                   Post Job Page form contest duration type field option to make
                  contest nonurgent price
                "
                [weight]="FontWeight.BOLD"
              >
                FREE
              </fl-text>
            </fl-radio-card-body>
          </fl-radio-card>
        </fl-col>
        <fl-col
          class="UrgentUpgrade-card"
          [col]="12"
          [colTablet]="6"
          [ngClass]="{ IsDisabled: upgradeFeesStatus?.error }"
        >
          <fl-radio-card
            flTrackingLabel="Urgent"
            i18n-pictureAlt="Urgent contest icon alt text"
            pictureAlt="Urgent contest icon"
            pictureSrc="job-post/urgent-contest-icon.svg"
            [control]="control"
            [isSelected]="isUrgentCardSelected$ | async"
            [value]="DurationType.URGENT"
            (click)="cardSelected.emit(DurationType.URGENT)"
          >
            <fl-radio-card-body>
              <fl-text
                i18n="
                   Post Job Page form contest duration type - urgent contest
                  option title
                "
                [size]="TextSize.SMALL"
                [weight]="FontWeight.BOLD"
              >
                1 day
              </fl-text>
              <fl-text
                i18n="
                   Post Job Page form contest duration type - urgent contest
                  option description
                "
                [flMarginBottom]="Margin.XSMALL"
              >
                Receive faster response from freelancers and get great entries
                returned to you in just 1 day!
              </fl-text>
              <fl-bit class="UrgentUpgrade-footer">
                <ng-container
                  *ngIf="
                    upgradeFees &&
                      upgradeFeesStatus &&
                      upgradeFeesStatus.ready &&
                      !upgradeFeesStatus.error;
                    else loadingUrgentPrice
                  "
                >
                  <fl-text [weight]="FontWeight.BOLD">
                    {{
                      upgradeFees.urgentPrice
                        | flCurrency: selectedCurrency.code
                    }}
                  </fl-text>
                </ng-container>
                <ng-template #loadingUrgentPrice>
                  <fl-loading-text
                    class="UrgentUpgrade-footer-loadingPrice"
                    [rows]="1"
                    [padded]="false"
                  ></fl-loading-text>
                </ng-template>
                <fl-upgrade-tag
                  [upgradeType]="UpgradeType.URGENT"
                ></fl-upgrade-tag>
              </fl-bit>
            </fl-radio-card-body>
          </fl-radio-card>
        </fl-col>
      </fl-grid>
    </fl-bit>
  `,
  styleUrls: ['./duration-type.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContestDurationTypeComponent implements OnChanges, OnInit {
  DurationType = DurationType;
  TextSize = TextSize;
  FontWeight = FontWeight;
  HeadingType = HeadingType;
  Margin = Margin;
  UpgradeType = UpgradeType;
  VerticalAlignment = VerticalAlignment;

  @Input() control: FormControl;
  @Input() upgradeFees: ContestFee;
  @Input() upgradeFeesStatus: RequestStatus<ContestFeesCollection>;
  @Input() selectedCurrency: Currency;
  @Output() cardSelected: EventEmitter<DurationType> = new EventEmitter();

  isNonUrgentCardSelected$: Rx.Observable<boolean>;
  isUrgentCardSelected$: Rx.Observable<boolean>;

  ngOnInit() {
    this.isNonUrgentCardSelected$ = this.control.valueChanges.pipe(
      startWith(this.control.value),
      map(durationType => durationType === DurationType.NONURGENT),
    );

    this.isUrgentCardSelected$ = this.control.valueChanges.pipe(
      startWith(this.control.value),
      map(durationType => durationType === DurationType.URGENT),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.upgradeFeesStatus &&
      changes.upgradeFeesStatus.currentValue &&
      changes.upgradeFeesStatus.currentValue.error &&
      this.control.value === DurationType.URGENT
    ) {
      this.control.setValue(DurationType.NONURGENT);
      this.cardSelected.emit(DurationType.NONURGENT);
    }
  }
}
